<template>
  <div class="card border-0 bg-white profile-settings-card rounded-0">
    <div class="card-body p-15 p-sm-20 p-md-25 p-lg-30 letter-spacing">
      <form v-on:submit="submit">
        <div class="row">
          <!-- LANG -->
          <div class="col-md-6" v-if="update">
            <div class="form-group mb-15 mb-sm-20 mb-md-25" v-if="locales.length > 1">
              <label class="d-block text-black fw-semibold mb-10">
                {{ $t('feature.contents.lang') }}
              </label>
              <div class="d-flex gap-3 align-items-center">
                <country-flag :country="lang" size="big" />
                <select class="form-select shadow-none text-black p-15 pe-50 fs-14 fw-medium w-auto" v-model="lang"
                  @change="changeLang()" required>
                  <option v-for="locale in locales" :value="locale" :key="locale">{{
                    $t(`feature.contents.$lang.${locale}`) }}</option>
                </select>
              </div>
              <!-- ERRORS -->
              <div class="form-messages mt-2 px-1" v-if="errors?.lang ?? false">
                <p v-for="error in errors.lang" :key="error" class="small text-danger lh-1 m-0 mt-1">{{
                  error }}</p>
              </div>
            </div>
          </div>
          <div class="col-12"></div>
          <!-- NAME -->
          <div class="col-md-6">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                {{ $t('form.name.label') }}*
              </label>
              <input v-model="category.name![lang]" class="form-control shadow-none text-black" required
                :class="[{ 'is-invalid': submitted && errors?.name }]" />
              <!-- ERRORS -->
              <div class="form-messages mt-2 px-1" v-if="errors?.name ?? false">
                <p v-for="error in errors.name" :key="error" class="small text-danger lh-1 m-0 mt-1">{{
                  error }}</p>
              </div>
            </div>
          </div>
          <div class="col-12"></div>
          <!-- DESCRIPTION -->
          <div class="col-md-6">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                {{ $t('form.description.label') }}
              </label>
              <textarea v-model="category.description![lang]" class="form-control shadow-none text-black"
                :class="[{ 'is-invalid': submitted && errors?.description }]" rows="6"></textarea>
              <!-- ERRORS -->
              <div class="form-messages mt-2 px-1" v-if="errors?.description ?? false">
                <p v-for="error in errors.description" :key="error" class="small text-danger lh-1 m-0 mt-1">{{
                  error }}</p>
              </div>
            </div>
          </div>
          <div class="col-12"></div>
          <!-- VISIBILITY -->
          <div class="col-md-6">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                {{ $t('feature.contents.$categories.visibility') }}*
              </label>
              <div>
                <select class="form-select shadow-none text-black fs-md-15" v-model="category.allow" required
                  :class="[{ 'is-invalid': submitted && errors?.allow }]">
                  <option v-for="visibility in visibility_options" :key="visibility" :value="visibility">{{
                    $t(`feature.contents.$categories.$visibility.${visibility}`) }}</option>
                </select>


              </div>
              <!-- ERRORS -->
              <div class="form-messages mt-2 px-1" v-if="errors?.allow ?? false">
                <p v-for="error in errors.allow" :key="error" class="small text-danger lh-1 m-0 mt-1">{{
                  error }}</p>
              </div>
              <div class="small text-muted mt-15">*{{ $t('feature.contents.$categories.visibility_checkbox_message') }}
              </div>
            </div>
          </div>


          <!-- COVER SELECT -->
          <div class="col-12"></div>
          <div class="col-md-6">

            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                {{ $t('feature.contents.$categories.imageCover') }}
              </label>
              <div>
                <input class="form-control fs-15 text-black shadow-none" type="file" id="cover-picker"
                  @change="event => fileUploaded(event)" accept="image/png, image/jpeg" ref="coverPickerRef">
                <div class="col-12 mt-25" v-if="category.icon_b64 && category.icon_b64 !== ' '">
                  <div class="position-relative">
                    <div class="position-absolute end-0 z-1 m-10">
                      <div data-bs-toggle="modal" :data-bs-target="`#deleteCover`"
                        class="align-items-center bg-danger-subtle d-inline-flex fs-3 h-50 justify-content-center rounded-circle text-danger w-50 cursor-pointer">
                        <i class="ph-bold ph-x"></i>
                      </div>
                    </div>
                    <img :src="category.icon_b64" alt="image">
                  </div>
                </div>
              </div>
              <!-- ERRORS -->
              <div class="form-messages mt-2 px-1" v-if="errors?.icon_b64 ?? false">
                <p v-for="error in errors.icon_b64" :key="error" class="small text-danger lh-1 m-0 mt-1">{{
                  error }}</p>
              </div>
            </div>
            <!-- <small class="text-muted">*{{ $t('feature.contents.$categories.visibility_checkbox_message') }}</small> -->
          </div>




          <div class="col-12" v-if="loading">
            <SpinnerLoading />
          </div>
          <div class="col-md-12" v-else>
            <div class="d-flex align-items-center justify-content-between gap-3">
              <!-- DELETE -->
              <button v-if="update" type="button" class="bg-transparent p-0 border-0 text-danger lh-1 fw-medium"
                data-bs-toggle="modal" data-bs-target="#confirmModal">
                <i class="ph ph-trash lh-1 me-1 position-relative top-2"></i>
                <span class="position-relative">{{ $t('cta.delete_category') }}</span>
              </button>
              <span v-else></span>
              <!-- SAVE -->
              <div class="d-flex flex-wrap gap-3 justify-content-end ms-auto">
                <DiscardButton @confirm="$router.push({ path: '/contents' })" />
                <button
                  class="default-btn transition fw-medium text-white pt-10 pb-10 ps-25 pe-25 pt-md-11 pb-md-11 ps-md-35 pe-md-35 fs-md-15 fs-lg-16"
                  type="submit" v-on:click="submitted = true">
                  {{ $t('cta.save_category') }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  </div>
  <!-- ERROR MESSAGE -->
  <div class="row justify-content-center">
    <div class="col">
      <div
        class="alert alert-danger alert-dismissible fade border-0 border-danger-subtle border-top rounded-0 m-0 mb-25"
        role="alert" id="errorMessage">
        <strong>{{ $t('error.something_went_wrong') }}</strong>
        <button type="button" class="btn-close" v-on:click="hideError" aria-label="Close"></button>
      </div>
    </div>
  </div>
  <ConfirmModal @confirm="deleteContentCategory()" :title="$t('cta.$delete.title')"
    :message="$t('cta.$delete.message')" />
  <ConfirmModal @confirm="deleteCover()" :id="`deleteCover`" :title="$t('cta.$delete.title')"
    :message="$t('cta.$delete.message')" />

</template>

<script lang="ts">
import { defineComponent, ref } from "vue";
import ConfirmModal from "@/components/Widgets/ConfirmModal.vue";
import SpinnerLoading from "@/components/Widgets/Spinner.vue";
import { removeNullProperties } from "@/utils/methods/remove_null_properties";
import API from "@/services/api/api.service";
import { ContentCategory, ContentCategoryVisibility } from "@/utils/models/content.model";
import getBase64 from "@/utils/methods/get_base_64";
import DiscardButton from "@/components/Widgets/DiscardButton.vue";
import config from "@/utils/const/config";
import { i18n } from '@/assets/i18n/i18n';
import CountryFlag from 'vue-country-flag-next';

export default defineComponent({
  setup() {
    const coverPickerRef = ref();
    return {
      coverPickerRef,
    };
  },
  name: "ContentCategoryForm",
  components: {
    ConfirmModal,
    SpinnerLoading,
    DiscardButton,
    CountryFlag
  },
  data: function () {
    const _category = this.$route.meta.category as ContentCategory;
    return {
      category: {
        ..._category,
        name: _category?.name ?? {},
        description: _category?.description ?? {}
      } as ContentCategory,
      submitted: false,
      loading: false,
      errors: undefined as any,
      visibility_options: ['all', 'restricted'] as ContentCategoryVisibility[],
      lang: (this.$route.query.lang ?? i18n.global.locale) as string,
    }
  },
  computed: {
    update(): boolean {
      return this.$route.meta.category !== undefined && this.$route.meta.category !== null;
    },
    locales(): string[] {
      return config.available_locales
    }
  },
  methods: {
    submit(event): void {
      this.errors = undefined;
      event.preventDefault();
      console.log("CATEGORY", this.category);
      removeNullProperties<ContentCategory>(this.category);

      this.loading = true;
      if (this.update) {
        console.log('UPDATE');
        console.log(this.category);
        Promise.all([
          API.updateContentCategoryLocalization(this.category, this.lang),
          API.updateContentCategory({
            ...this.category,
            name: this.category.name?.[i18n.global.locale],
            description: undefined,
          } as ContentCategory),
        ]).then((responses) => {
          if (responses.every((res) => res.data.success === true)) {
            this.$router.push(`/contents`);
          }
        }).catch((error) => {
          this.errors = error.response?.data?.errors;
          this.showError();
        }).finally(() => {
          this.loading = false;
        });
      } else {
        console.log('CREATE');

        API.createContentCategoryLocalization(this.category).then((response) => {
          this.$router.push(`/contents`);
        }).catch((error) => {
          this.errors = error.response?.data?.errors;
          this.showError();
        }).finally(() => {
          this.loading = false;
        });
      }
    },
    deleteContentCategory(): void {
      this.loading = true;
      API.deleteContentCategory(this.category.id!).then((response) => {
        if (response.data.success === true) {
          this.$router.push('/contents');
        }
      }).catch((error) => {
        this.showError();
      }).finally(() => {
        console.log("DELETED_CATEGORY_ID", this.category.id!);
        this.loading = false;
      })
    },
    showError() {
      const error = document.getElementById('errorMessage');
      error?.classList.add('show');
      setTimeout(() => {
        this.hideError();
      }, 5000)
    },
    hideError() {
      const error = document.getElementById('errorMessage');
      error?.classList.remove('show');
    },
    async fileUploaded($event) {
      if ($event.target.value) {
        this.category.icon_b64 = await getBase64($event.target.files[0]);
      }
    },
    deleteCover() {
      this.category.icon_b64 = " ";
      if (this.coverPickerRef) {
        this.coverPickerRef.value = null;
      }
    },
    changeLang(lang?: string) {
      console.log(lang);
      if (lang !== undefined) {
        this.lang = lang;
      }
    }
  },
});
</script>